import { isEmpty } from 'lodash';
import React from 'react';
import styled from 'styled-components';
import { User, UserStatus, UserType } from 'api/models';
import { MembersMeta, Project } from 'api/project';
import { UserProfileInfo } from 'api/user';
import { Avatar } from 'components/atoms/avatar';
import { Membership, Memberships } from 'stores/memberships';
import { getUserLabel } from 'utils/get-user-label';
import { sortByText } from 'utils/sort-by-text';
import { MemberDetails } from './member-details';
import { MoreActions } from './more-actions';
import { Status } from './status';

const AvaterContainer = styled.div`
  padding-left: var(--spacing-03);
`;

interface ColumnList {
  project: Project;
  loggedInUser: UserProfileInfo;
  memberships: Memberships;
  loggedInUserPermissions: Membership | undefined;
  membersMeta: MembersMeta | undefined;
}
export const columnsList = ({
  project,
  loggedInUserPermissions,
  membersMeta,
  loggedInUser,
  memberships,
}: ColumnList) => [
  {
    key: 'logo',
    dataIndex: 'photoUrl',
    width: 40,
    render(src: string, user: User) {
      if (src || user) {
        return (
          <AvaterContainer>
            <Avatar src={src} name={getUserLabel(user)} />
          </AvaterContainer>
        );
      }

      return <AvaterContainer>-</AvaterContainer>;
    },
  },
  {
    key: 'teamMembers',
    title: 'Team Members',
    render(memberDetails: User) {
      return (
        <MemberDetails member={memberDetails} projectId={project.projectId} />
      );
    },
    sorter: (a: User, b: User) => sortByText(getUserLabel(a), getUserLabel(b)),
  },
  {
    width: 264,
    dataIndex: 'emailId',
    title: 'Email',
    ellipsis: true,
    render(email: string) {
      return email || <>-</>;
    },
    sorter: (a: User, b: User) => sortByText(a.emailId, b.emailId),
  },
  {
    width: 160,
    dataIndex: 'userStatus',
    title: 'Status',
    render(status: UserStatus, user: User) {
      return <Status status={status} user={user} memberships={memberships} />;
    },
    sorter(a: User, b: User) {
      const aJoinedAt = memberships.byUserId[a.userId]?.joinedAt;
      const bJoinedAt = memberships.byUserId[b.userId]?.joinedAt;

      if (!aJoinedAt) {
        return -1;
      }

      if (!bJoinedAt) {
        return 1;
      }

      return aJoinedAt - bJoinedAt;
    },
  },
  {
    key: 'actions',
    width: 72,
    render(text: any, user: User) {
      /**
       * 1. If the loggedIn user is external
       * 1.1 [AND] if the current user type is not EXTERNAL
       * [OR] loggedInUserPermissions is empty
       * if any of above dont show the actions
       */
      if (
        (loggedInUser.user.userType === 'EXTERNAL' && // 1
          loggedInUser.user.userType !== user.userType) || // 1.1
        isEmpty(loggedInUserPermissions) || // 2
        (loggedInUser.user.userType === UserType.Partner &&
          loggedInUser.user.company.companyId !== user.company.companyId)
      ) {
        return <></>;
      }

      return (
        <MoreActions
          projectId={project.projectId}
          user={user}
          loggedInUserPermissions={loggedInUserPermissions}
          membersMeta={membersMeta}
          projectOwner={project.projectOwner}
          customerChampion={project?.customerChampion}
          loggedInUser={loggedInUser}
          memberships={memberships}
        />
      );
    },
  },
];
